"use client";

import { getParentState } from "@hopper-b2b/hopper-utils";
import { useI18nContext } from "@hopper-b2b/i18n";
import { AtHotelLogo, LoadingContent } from "@hopper-b2b/ui-core";
import { isAtHotelBrand } from "@hopper-b2b/utilities";
import { useMemo } from "react";
import { useAuthState } from "../../machine";
import { getIsLoading } from "../../machine/selectors";
import {
  AuthErrorContent,
  FinalizeForm,
  MergeAccountModal,
  SuccessModal,
  VerifyEmailModal,
  VerifyPhoneModal,
} from "./components";
import { AuthFlowProps, LandingForm } from "./AuthFlow";

/**
 * Maps the current XState auth machine state to the appropriate UI component.
 * Shared between AuthPrompt (modal) and AuthFlow (standalone page).
 */
export const AuthStateContent = ({
  onClose,
  loginContextHeader,
  googleIdentityClientId,
  isGoogleSsoAuthEnabled,
}: AuthFlowProps) => {
  const { t } = useI18nContext();
  const [state] = useAuthState();
  const parentState = getParentState(state.value);
  const loading = getIsLoading(state.value);

  const Content = useMemo(() => {
    if (loading) {
      return (
        <LoadingContent
          message={t("loading")}
          loadingImg={
            isAtHotelBrand() && (
              <AtHotelLogo color={"#17803C"} width={180} height={32} />
            )
          }
        />
      );
    }

    switch (parentState) {
      case "landing":
        return (
          <LandingForm
            loginContextHeader={loginContextHeader}
            googleIdentityClientId={googleIdentityClientId}
            isGoogleSsoAuthEnabled={isGoogleSsoAuthEnabled}
          />
        );
      case "googleLogin":
        return null;
      case "phoneLogin":
        return <VerifyPhoneModal />;
      case "emailLogin":
        return <VerifyEmailModal />;
      case "finalize":
        return <FinalizeForm />;
      case "success":
        return <SuccessModal onClose={onClose} />;
      case "error":
        return <AuthErrorContent onClose={onClose} />;
      case "merge":
        return <MergeAccountModal />;
      default:
        onClose?.();
        return null;
    }
  }, [
    loading,
    onClose,
    parentState,
    t,
    loginContextHeader,
    googleIdentityClientId,
    isGoogleSsoAuthEnabled,
  ]);

  return Content;
};
